import { useData } from "../../runtime/provider";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LInput from "@components/LInput";
import LText from "@components/LText";
import { useAlbums } from "@hooks/trpc/useAlbums";
import { useLibrary } from "@hooks/trpc/useLibrary";
import { useMe } from "@hooks/trpc/useMe";
import { useNotinterested } from "@hooks/trpc/useNotInterested";
import { usePlaylist } from "@hooks/trpc/usePlaylists";
import { getCountryCode } from "@utils/region";
import { ValidTerritoryCode } from "@libs/utils";
import { getTodaysTopHitsId } from "@libs/utils";
import { getActiveTab } from "@hooks/useActiveTab";
import { useShare } from "@hooks/trpc/useShare";
import { router } from "expo-router";
import { forwardRef, useRef, useState, useEffect, useMemo } from "react";
import {
  TextInput,
  ScrollView,
  ActivityIndicator,
  AlertButton,
  useWindowDimensions,
} from "react-native";
import LIcon, { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import { pickImage } from "@components/ImagePicker";
import { uploadImage } from "@components/Account/Profile/profile.utils";
import useInternetStatus from "@utils/internetStatus";
import type { CatalogCollectionType } from "@utils/player";
import Alert from "@components/Alert";
import { getViewPort } from "@utils/web/config";
import { toast } from "sonner-native";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";
import { BottomSheetPlainLayout } from "@utils/swmModal/components/BottomSheetPrimitives";

type CollectionOptionsParams = {
  collectionId: string;
  collectionType: Exclude<CatalogCollectionType, "artist">;
};

type CollectionOptionsContentProps = CollectionOptionsParams & {
  closeModal: () => void;
};

interface RenamePlaylistProps {
  id: string;
  setIsRenaming: (isRenaming: boolean) => void;
  title?: string;
}

const RenamePlaylist = ({ id, title, setIsRenaming }: RenamePlaylistProps) => {
  const { updatePlaylist } = usePlaylist(id);

  const [newTitle, setNewTitle] = useState(title);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <>
      <LInput
        ref={inputRef}
        value={newTitle}
        onChangeText={(text) => setNewTitle(text)}
        selectTextOnFocus
        style={{ width: "100%", textAlign: "center" }}
      />
      <LBox
        mt="m1"
        flexDirection="row"
        width="100%"
        px="l1"
        justifyContent="space-evenly"
      >
        <LButton
          sentryEventName="rename_playlist_save_button"
          variant="tertiary"
          label="Save"
          onPress={async () => {
            if (title === newTitle) {
              setIsRenaming(false);
              return;
            }

            const trimmedTitle = newTitle?.trim();
            if (!trimmedTitle || trimmedTitle.length === 0) {
              Alert.alert("Please enter a valid playlist name");
              return;
            }

            await updatePlaylist.mutateAsync({ id, title: trimmedTitle });

            setIsRenaming(false);
          }}
          width={100}
        />
        <LButton
          sentryEventName="rename_playlist_cancel_button"
          variant="tertiary"
          label="Cancel"
          onPress={async () => {
            setNewTitle(title);
            setIsRenaming(false);
          }}
          width={100}
        />
      </LBox>
    </>
  );
};

export const CollectionOptions = ({
  closeModal,
  collectionId,
  collectionType,
}: CollectionOptionsContentProps) => {
  const session = useData();
  const isConnected = useInternetStatus();

  const {
    query: { data: albumsData },
  } = useAlbums(
    collectionType === "album" && collectionId ? [collectionId] : undefined,
  );
  const {
    query: { data: playlistData },
  } = usePlaylist(
    collectionType === "playlist" && collectionId ? collectionId : "",
  );

  const { toggleCollectionLike } = useLibrary();
  const { deletePlaylist } = usePlaylist(collectionId, {
    enabled: collectionType === "playlist",
  });
  const notInterested = useNotinterested();
  const share = useShare();

  const title = useMemo(() => {
    switch (collectionType) {
      case "album":
        return albumsData?.at(0)?.title;
      case "playlist":
        return playlistData?.title;
      case undefined:
        return "";
    }
  }, [albumsData, collectionType, playlistData]);

  const [isRenaming, setIsRenaming] = useState(false);
  const me = useMe();

  const {
    query: { data: user },
  } = me;
  const countryCode =
    user?.plan?.countryId ??
    user?.countryCode ??
    (getCountryCode() as ValidTerritoryCode);
  const collectionIsCustom =
    collectionId === getTodaysTopHitsId(countryCode) ||
    collectionId === "discover_daily" ||
    collectionId.startsWith("liked_");

  const isOwnPlaylist =
    collectionType === "playlist" && playlistData?.user.id === user?.id;

  const { updatePlaylist } = usePlaylist(collectionId, {
    enabled: isOwnPlaylist,
  });

  const [isUpdatingArtwork, setIsUpdatingArtwork] = useState(false);

  const { blurhash, image, isOwner, ownerText } = useMemo(() => {
    switch (collectionType) {
      case "album":
        return {
          image: albumsData?.at(0)?.image,
          isOwner: albumsData?.at(0)?.artistIds?.find((c) => c === user?.id),
          ownerText: albumsData?.at(0)?.artistDisplayText,
        };
      case "playlist": {
        const pl = playlistData;
        let ownerText = pl?.user?.name;

        if (collectionId.startsWith("liked_")) {
          ownerText = "by You";
        } else if (pl?.user?.id === user?.id) {
          ownerText = "by You";
        } else if (collectionId === "discover_daily") {
          ownerText = "by Lissen";
        } else {
          ownerText = `Curator • ${pl?.user?.name}`;
        }

        return {
          image: pl?.images,
          blurhash: pl?.blurhash,
          ownerText,
          isOwner: pl?.user?.id === user?.id,
        };
      }
      default:
        throw new Error(`Invalid collectionType: ${collectionType}`);
    }
  }, [albumsData, collectionId, collectionType, playlistData, user?.id]);

  const handlePressDeletePlaylist = async () => {
    if (!collectionId || !collectionType || !playlistData) {
      return;
    }

    return Alert.alert(
      "Delete Playlist",
      "Are you sure you want to delete this playlist?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await deletePlaylist.mutateAsync(collectionId);

              toggleCollectionLike(collectionId, collectionType);
              closeModal();

              if (router.canGoBack()) {
                router.back();
              } else {
                router.replace(`/(app)/(tabs)/${getActiveTab()}`);
              }

              toast.success("Playlist deleted!");
            } catch {
              toast.error("Failed to delete playlist");
            }
          },
        },
      ],
    );
  };

  const isPrivatePlaylist =
    collectionType === "playlist" && playlistData?.visibility === "private";

  const handlePressNotInterested = () => {
    void notInterested({
      id: collectionId,
      type: collectionType,
    });

    closeModal();
    toast.success("Thanks for your feedback.");
  };

  const handlePressTogglePlaylistVisibility = async () => {
    if (!playlistData) return;

    try {
      await updatePlaylist.mutateAsync({
        id: collectionId,
        visibility:
          playlistData.visibility === "private" ? "public" : "private",
      });

      toast.success("Playlist updated!");
    } catch (error) {
      console.log(error);
    }
  };

  const handlePressShare = () => {
    let directLink;

    switch (collectionType) {
      case "album": {
        const album = albumsData?.at(0);
        if (!album) return;
        directLink = {
          contentType: "album" as const,
          data: {
            id: album.id,
            title: album.title,
            artistDisplayText: album.artistDisplayText,
            imageUrl: album.image ?? null,
          },
        };
        break;
      }
      case "playlist": {
        const playlist = playlistData;
        if (!playlist) return;
        directLink = {
          contentType: "playlist" as const,
          data: {
            id: playlist.id,
            title: playlist.title,
            user: {
              id: playlist.user?.id,
              name: playlist.user?.name,
            },
            imageUrl: playlist.images?.at(0) ?? null,
          },
        };
        break;
      }
      default: {
        return;
      }
    }

    closeModal();
    share.mutate({
      linkType: "direct-link",
      ...directLink,
    });
  };

  const handlePressEditArtwork = () => {
    const options: AlertButton[] = [
      {
        text: "Cancel",
        style: "cancel" as const,
      },
      {
        text: "Choose from library",
        isPreferred: true,
        style: "default" as const,
        async onPress() {
          await pickImage(async (url) => {
            // onSuccess
            setIsUpdatingArtwork(true);

            try {
              const imageUrl = await uploadImage(session, {
                uri: url.uri,
                contentType: url.mimeType,
              });

              await updatePlaylist.mutateAsync({
                id: collectionId,
                image: imageUrl,
              });

              toast.success("Playlist updated", { duration: 1500 });
              setIsUpdatingArtwork(false);
            } catch (error) {
              console.error(error);
              toast.error("Failed to update playlist");
              setIsUpdatingArtwork(false);
            }
          });
        },
      },
    ];

    if (playlistData?.hasCustomImage) {
      options.push({
        text: "Remove current image",
        isPreferred: false,
        style: "destructive" as const,
        async onPress() {
          setIsUpdatingArtwork(true);
          try {
            await updatePlaylist.mutateAsync({
              id: collectionId,
              image: null,
            });

            toast.success("Playlist updated", { duration: 1500 });
            setIsUpdatingArtwork(false);
          } catch (error) {
            console.error(error);
            toast.error("Failed to update playlist");
            setIsUpdatingArtwork(false);
          }
        },
      });
    }

    Alert.alert("Edit image", undefined, options);
  };

  const window = useWindowDimensions();
  const { width } = getViewPort(window);
  const artworkSize = Math.min(width * 0.34, 148);

  return (
    <BottomSheetPlainLayout contentProps={{ gap: "m1", pb: "s4", pt: "s3" }}>
      <LBox>
        <LBox alignItems={"center"}>
          <LPressable
            onPress={handlePressEditArtwork}
            disabled={
              isUpdatingArtwork ||
              updatePlaylist.isPending ||
              !isOwnPlaylist ||
              !isConnected
            }
            sentryEventName="edit_playlist_artwork"
          >
            <LBox borderRadius="l" overflow="hidden">
              <LArtwork
                url={image}
                imageSize="medium"
                size={artworkSize}
                blurhash={blurhash}
              />
              {isOwnPlaylist ? (
                <LIcon
                  name="brush"
                  size={32}
                  position="absolute"
                  bottom={4}
                  right={4}
                />
              ) : null}
              {isUpdatingArtwork ? (
                <ActivityIndicator
                  size="large"
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                  }}
                />
              ) : null}
            </LBox>
          </LPressable>
        </LBox>
      </LBox>
      <LBox alignItems="center">
        {!isRenaming && title ? (
          <LBox gap="s1">
            <LText color="black" textAlign="center" variant="h1">
              {title}
            </LText>
            {ownerText ? (
              <LText
                color="blackTranslucent70"
                textAlign="center"
                variant="bodyL"
              >
                {ownerText}
              </LText>
            ) : null}
          </LBox>
        ) : collectionType === "playlist" && playlistData && collectionId ? (
          <RenamePlaylist
            id={collectionId}
            title={title}
            setIsRenaming={setIsRenaming}
          />
        ) : null}
      </LBox>
      <ScrollView
        bounces={false}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        style={{ maxHeight: width * 0.85 }}
      >
        {collectionType === "playlist" && isOwner ? (
          <>
            <CollectionOptionRow
              sentryEventName="delete_playlist"
              icon="trash"
              strokeWidth={2}
              text="Delete Playlist"
              enabled={!!isConnected}
              onPress={handlePressDeletePlaylist}
            />
            <CollectionOptionRow
              sentryEventName="rename_playlist"
              icon="rename"
              text="Rename Playlist"
              enabled={!!isConnected}
              onPress={() => {
                setIsRenaming((prev) => !prev);
              }}
            />
            {collectionType === "playlist" && playlistData && collectionId ? (
              <CollectionOptionRow
                sentryEventName="playlist_visibility_toggle"
                icon={
                  playlistData.visibility === "private"
                    ? "globe"
                    : "lock_locked"
                }
                text={`Make Playlist ${
                  playlistData.visibility === "private" ? "Public" : "Private"
                }`}
                enabled={
                  !updatePlaylist.isPending && !!playlistData && !!isConnected
                }
                onPress={handlePressTogglePlaylistVisibility}
              />
            ) : null}
          </>
        ) : null}
        {!(collectionIsCustom || isOwner) ? (
          <CollectionOptionRow
            sentryEventName="not_interested"
            icon="cross"
            strokeWidth={2}
            text="Not Interested"
            onPress={handlePressNotInterested}
            enabled={!!isConnected}
          />
        ) : null}
        {!isPrivatePlaylist ? (
          <CollectionOptionRow
            sentryEventName="share_collection"
            icon="share"
            strokeWidth={2}
            text="Share"
            onPress={handlePressShare}
            enabled={!share.isPending}
          />
        ) : null}
      </ScrollView>
    </BottomSheetPlainLayout>
  );
};

export default CollectionOptions;

type CollectionOptionRowProps = {
  icon: IconName;
  text: string;
  onPress?: (() => void) | (() => Promise<void>);
  strokeColour?: string;
  strokeWidth?: number;
  enabled?: boolean;
  sentryEventName: string;
};

const CollectionOptionRow = ({
  enabled = true,
  icon,
  onPress,
  sentryEventName,
  strokeColour,
  strokeWidth,
  text,
}: CollectionOptionRowProps) => (
  <LPressable
    disabled={!enabled}
    onPress={onPress}
    sentryEventName={sentryEventName}
  >
    <LBox
      alignItems="center"
      flexDirection="row"
      gap="s3"
      opacity={enabled ? 1 : 0.5}
      py="s3"
    >
      <LBox
        alignItems="center"
        backgroundColor="blackTranslucent70"
        borderRadius="max"
        height={36}
        justifyContent="center"
        width={36}
      >
        <LIcon
          fill="white"
          name={icon}
          size={20}
          stroke={strokeColour ?? "white"}
          strokeWidth={strokeWidth}
        />
      </LBox>
      <LText color="black" variant="bodyM2m">
        {text}
      </LText>
    </LBox>
  </LPressable>
);

export const CollectionOptionsSWMSheet = forwardRef<
  SheetAdapterRef,
  CollectionOptionsParams
>(function CollectionOptionsSWMSheet({ collectionId, collectionType }, ref) {
  return (
    <AppModalSheet keyboardBehavior="inset" ref={ref} showHandle={false}>
      {({ closeModal }) => (
        <CollectionOptions
          closeModal={closeModal}
          collectionId={collectionId}
          collectionType={collectionType}
        />
      )}
    </AppModalSheet>
  );
});
